import type { WorkspaceReference, ResolvedReference } from "./workspace.api";
import { http } from "./http";
export type Visibility = "PRIVATE" | "SELECTED_MEMBERS" | "SCHOOL";
export type NoteContent = {
  title: string;
  body: string;
  visibility: Visibility;
  memberIds: string[];
  references?: WorkspaceReference[];
  mentionIds?: string[];
  fileIds?: string[];
  resolvedReferences?: ResolvedReference[];
  mentions?: Array<{ id: string; firstName: string; lastName: string }>;
  files?: Array<{ id: string; name: string; mime: string; size: number }>;
};
export type NoteSummary = {
  references?: WorkspaceReference[];
  body: string;
  id: string;
  title: string;
  visibility: Visibility;
  version: number;
  canEdit: boolean;
};
export type Note = NoteContent & NoteSummary & { authorName: string };
export type RecoverableNote = {
  id: string;
  version: number;
  visibility: Visibility;
  updatedAt: string;
};
export type RecoveryEvent = {
  noteId: string;
  actorId: string;
  previousOwnerId: string;
  newOwnerId: string;
  reason: string;
  version: number;
  createdAt: string;
};
export const notesApi = {
  notifications: async (page = 1) =>
    (
      await http.get<{
        items: Array<{
          id: string;
          noteId: string;
          title: string;
          read: boolean;
          createdAt: string;
        }>;
        hasMore: boolean;
      }>("/v1/workspace/notes/notifications", { params: { page } })
    ).data,
  readNotification: async (id: string) =>
    http.post("/v1/workspace/notes/notifications/" + id + "/read"),
  upload: async (id: string, file: File, requestId: string) => {
    const data = new FormData();
    data.append("file", file);
    data.append("requestId", requestId);
    return (
      await http.post<{ id: string }>(
        "/v1/workspace/notes/" + id + "/files",
        data,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 20000 },
      )
    ).data;
  },
  download: async (id: string, file: string, version?: number) =>
    (
      await http.get<Blob>("/v1/workspace/notes/" + id + "/files/" + file, {
        params: { version },
        responseType: "blob",
      })
    ).data,
  history: async (id: string, page: number) =>
    (
      await http.get<{
        items: Array<{
          version: number;
          snapshot: NoteContent;
          createdAt: string;
        }>;
        hasMore: boolean;
      }>("/v1/workspace/notes/" + id + "/history", { params: { page } })
    ).data,
  recoverable: async (page: number) =>
    (
      await http.get<{ items: RecoverableNote[]; hasMore: boolean }>(
        "/v1/workspace/notes/recoverable",
        { params: { page } },
      )
    ).data,
  recoveries: async (page: number) =>
    (
      await http.get<{ items: RecoveryEvent[]; hasMore: boolean }>(
        "/v1/workspace/notes/recoveries",
        { params: { page } },
      )
    ).data,
  recoveryCode: async () => {
    await http.post("/v1/auth/mfa/email/send-code");
  },
  recover: async (
    id: string,
    data: {
      requestId: string;
      version: number;
      newOwnerId: string;
      reason: string;
      code: string;
    },
  ) => (await http.post("/v1/workspace/notes/" + id + "/recover", data)).data,
  list: async (page: number) =>
    (
      await http.get<{ items: NoteSummary[]; hasMore: boolean }>(
        "/v1/workspace/notes",
        { params: { page } },
      )
    ).data,
  get: async (id: string) =>
    (await http.get<Note>("/v1/workspace/notes/" + id)).data,
  members: async () =>
    (
      await http.get<
        Array<{
          id: string;
          role?: string;
          firstName: string;
          lastName: string;
        }>
      >("/v1/workspace/notes/members")
    ).data,
  create: async (data: NoteContent & { requestId: string }) =>
    (await http.post<{ id: string }>("/v1/workspace/notes", data)).data,
  update: async (
    id: string,
    data: NoteContent & { version: number; requestId?: string },
  ) => (await http.patch("/v1/workspace/notes/" + id, data)).data,
};
